import { yupResolver } from '@hookform/resolvers/yup';
import { Cancel, Search } from '@mui/icons-material';
import { Checkbox, Grid, Stack, styled } from '@mui/material';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { object, string } from 'yup';

import { timeSince } from '../../helper/DateOperations';
import ILocation from '../../types/ILocation';
import WsColors from '../../WsColors';
import EmailFooter from '../EmailFooter';
import { Item } from '../Item';
import { SearchBar } from '../SearchBar';
import { WsButton } from '../WsButton';
import { WsDetailsCard, WsDetailsProps } from '../WsDetailsCard';
import { WsPagination } from '../WsPagination';
import { WsTypography } from '../WsTypography';

const SearchBarContainer = styled(Item)(({ theme }) => ({
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  paddingLeft: theme.spacing(0),
  paddingRigt: theme.spacing(0),
  width: '100%',
  boxShadow: '2px 2px 2px ' + WsColors.BorderGrey,
}));

export interface IJobSelectionProps<T> {
  multiple?: boolean;
  jobs: T[];
  totalCount: number;
  getSelectedJobs: (arg: string[]) => void;
  selectedJobTitle: (arg: string) => void;
  handleJobSearch: <R>(arg: R) => Promise<void>;
  handleChangeParameters: (query: string, page: number) => Promise<void>;
  handleClose: () => void;
}

enum SalaryTimeSpan {
  PerAnnum = 'PerAnnum',
  PerMonth = 'PerMonth',
  PerDay = 'PerDay',
  PerHour = 'PerHour',
  None = 'None',
}

enum JobType {
  Permanent = 'Permanent',
  Contract = 'Contract',
  Temporary = 'Temporary',
  FullTime = 'FullType',
  Internship = 'Internship',
  None = 'None',
  ContractToHireW2 = 'ContractToHireW2',
  ContractW2 = 'ContractW2',
  ContractIndependent = 'ContractIndependent',
  ContractToHireIndependent = 'ContractToHireIndependent',
  ContractCorpToCorp = 'ContractCorpToCorp',
  ContractToHireCorpToCorp = 'ContractToHireCorpToCorp',
  PartTime = 'PartTime',
  Freelance = 'Freelance',
  PermanentAndContract = 'PermanentAndContract',
  ThirdParty = 'ThirdParty',
}

interface IJob {
  title?: string;
  jobCode?: string;
  locations?: ILocation[];
  clientName?: string;
  minimumSalary?: number;
  maximumSalary?: number;
  salaryCurrency?: string;
  salaryTimeSpan?: SalaryTimeSpan;
  salaryAlternateDisplayText?: string;
  jobType?: JobType;
  numberOfPositions?: number;
  accountManagerEmail?: string;
  clientRecruiter?: string;
  clientJobCode?: string;
  postedOnJobBoards?: string[];
  skills?: string[];
  tags?: string[];
  createdBy?: string;
  postedDate?: Date;
}

const JobSelection = <T extends IJob>({
  multiple = true,
  jobs,
  totalCount,
  getSelectedJobs,
  selectedJobTitle,
  handleJobSearch,
  handleChangeParameters,
  handleClose,
}: IJobSelectionProps<T>) => {
  const schema = object().shape({
    jobSearchQuery: string(),
  });

  const defaultValues = {
    jobSearchQuery: '',
  };

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
  });

  const [selectedJobs, setSelectedJobs] = useState<string[]>([]);

  const importJob = () => getSelectedJobs(selectedJobs);

  const addJob = (jobCode: string) => setSelectedJobs((prev) => [...prev, jobCode]);

  const removeJob = (jobCode: string) =>
    setSelectedJobs((prev) => prev.filter((code) => code !== jobCode));

  const getJobCardDetails = (job: T): WsDetailsProps => ({
    title: job.title,
    isActiveCard: true,
    titleCode: job.jobCode,
    clientName: job.clientName,
    locationsArray: job.locations,
    detailsArray: [
      {
        labelName: `${job.salaryCurrency} ${job.minimumSalary} - ${job.maximumSalary} ${job.salaryTimeSpan}`,
      },
      {
        labelName: job.jobType ?? '',
      },
      {
        labelName: job.numberOfPositions ?? '',
      },
      {
        labelName: job.accountManagerEmail ?? '',
      },
      {
        labelName: job.clientRecruiter ?? '',
      },
      {
        labelName: job.clientJobCode ?? '',
      },
      {
        labelName: `${job.postedOnJobBoards?.length ?? 0} out of 6 boards`,
      },
    ],
    skillsArray: job.skills,
    tagsArray: job.tags,
    footer: (
      <Stack direction={'row'} p={2} justifyContent={'space-between'} alignItems={'center'}>
        <WsTypography>{`Posted by ${job.createdBy} ${timeSince(
          job?.postedDate?.toString() ?? ''
        )}`}</WsTypography>
        {multiple === false ? (
          <WsButton
            variant="outlined"
            onClick={() => {
              getSelectedJobs([job.jobCode ?? '']);
              selectedJobTitle(job.title ?? '');
            }}
          >
            Select Job
          </WsButton>
        ) : (
          <></>
        )}
      </Stack>
    ),
  });

  return (
    <FormProvider {...methods}>
      <Stack p={2} spacing={2} component="form" onSubmit={methods.handleSubmit(handleJobSearch)}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <SearchBarContainer>
            <SearchBar name="jobSearchQuery" placeholder="Search..." />
          </SearchBarContainer>
          <WsButton variant="outlined" type="submit" size="large" startIcon={<Search />}>
            Search
          </WsButton>
        </Stack>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction={'row'} spacing={0.5} alignItems={'center'}>
            <WsTypography>{`Showing ${
              totalCount > 10 ? 10 : totalCount
            } of ${totalCount} results`}</WsTypography>
          </Stack>
          <WsPagination
            count={Math.ceil(totalCount / 10)}
            onChange={(ev, page) => {
              handleChangeParameters(methods.getValues('jobSearchQuery'), page - 1);
            }}
          />
        </Stack>
      </Stack>
      <Stack p={2} spacing={2} mb={6}>
        {jobs?.map((job, i) => (
          <Grid container key={i}>
            {multiple && (
              <Grid item xs={0.8}>
                <Checkbox
                  checked={selectedJobs.includes(job.jobCode || '')}
                  onChange={(ev) =>
                    ev.target.checked ? addJob(job.jobCode || '') : removeJob(job.jobCode || '')
                  }
                />
              </Grid>
            )}
            <Grid item xs={multiple ? 11.2 : 12}>
              <WsDetailsCard {...getJobCardDetails(job)} />
            </Grid>
          </Grid>
        ))}
      </Stack>
      <EmailFooter>
        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
          <WsButton size="large" variant="outlined" startIcon={<Cancel />} onClick={handleClose}>
            Cancel
          </WsButton>
          {multiple ? (
            <WsButton size="large" onClick={importJob} disabled={selectedJobs.length === 0}>
              {'Select ' + selectedJobs.length + ' Job(s)'}
            </WsButton>
          ) : (
            <></>
          )}
        </Stack>
      </EmailFooter>
    </FormProvider>
  );
};

export default JobSelection;
